import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Calendar, Video, MapPin, ChevronRight } from "lucide-react";

/**
 * Widget "Próximos compromissos" — mostra ao consultor, no dashboard,
 * as visitas e videochamadas que ele agendou com famílias. Poupa o
 * consultor de abrir cada caso pra saber o que vem.
 */
export default function NextAppointmentsWidget() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/consultant/appointments/upcoming")
      .then(({ data }) => setItems(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading || items.length === 0) return null;
  const now = Date.now();
  return (
    <section className="bg-white border border-slate-200 rounded-xl p-5 mb-6" data-testid="next-appointments-widget">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display font-semibold text-lg text-[#0B2B62] flex items-center gap-2">
          <Calendar className="w-5 h-5" /> Próximos compromissos com famílias
        </h2>
        <span className="text-xs text-slate-500">{items.length} agendado{items.length > 1 ? "s" : ""}</span>
      </div>
      <ul className="space-y-2">
        {items.slice(0, 5).map((a) => {
          const Icon = a.type === "videochamada" ? Video : MapPin;
          const dt = new Date(a.when);
          const soon = dt.getTime() - now < 24 * 3600 * 1000;
          return (
            <li key={a.id} data-testid={`appt-${a.id}`}
              className={`p-3 rounded-md border flex items-center gap-3 ${soon ? "border-amber-300 bg-amber-50/40" : "border-slate-200"}`}>
              <div className={`w-10 h-10 rounded-md flex items-center justify-center shrink-0 ${a.type === "videochamada" ? "bg-blue-50 text-[#0B2B62]" : "bg-emerald-50 text-emerald-700"}`}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm text-[#0B2B62] flex items-center gap-1.5">
                  {a.patient_name}
                  <span className="text-[10px] font-mono text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                    {a.clinic_code}
                  </span>
                  {soon && <span className="text-[10px] font-bold text-amber-800 uppercase tracking-widest">próximas 24h</span>}
                </div>
                <div className="text-xs text-slate-500">
                  {a.type === "videochamada" ? "Videochamada" : "Visita presencial"} · {dt.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}
                  {a.clinic_city && ` · ${a.clinic_city}/${a.clinic_state}`}
                </div>
                {a.notes && <div className="text-[11px] text-slate-500 mt-0.5 truncate">{a.notes}</div>}
              </div>
              <Link to={`/consultor/caso/${a.case_id}`} data-testid={`appt-open-${a.id}`}
                className="shrink-0 text-xs text-[#0B2B62] hover:underline inline-flex items-center gap-0.5">
                Abrir caso <ChevronRight className="w-3 h-3" />
              </Link>
            </li>
          );
        })}
      </ul>
      {items.length > 5 && (
        <div className="mt-2 text-[11px] text-slate-500 text-right">+ {items.length - 5} outros agendamentos</div>
      )}
    </section>
  );
}
